import {combineReducers, configureStore} from '@reduxjs/toolkit';
import {apiV1, apiV2} from '@goproblems/shared/api';
import {
    API_BASE_CONFIG,
    API_V2_BASE_CONFIG,
    fetch,
    rtkQueryErrorLogger,
} from '@api/base';
import '@api/registerEndpoints';
import {
    aiSettingsSlice,
    aiStatusSlice,
    authSlice,
    boardSlice,
    featureFlagsSlice,
    globalBoardSettingsSlice,
    modals,
    uiSlice,
} from '@reducers';
import {setupListeners} from '@reduxjs/toolkit/query';
import problemCollectionSlice from '@reducers/problemCollection';
import {initialAuthState} from '@goproblems/shared/state/auth';
import {getStoredAuthState} from '@utils/auth';
import {authPersistence} from '@reducers/authPersistence';

export const reducers = {
    [apiV2.reducerPath]: apiV2.reducer,
    [apiV1.reducerPath]: apiV1.reducer,
    auth: authSlice.reducer,
    ui: uiSlice.reducer,
    board: boardSlice.reducer,
    globalBoardSettings: globalBoardSettingsSlice.reducer,
    aiSettings: aiSettingsSlice.reducer,
    featureFlags: featureFlagsSlice.reducer,
    aiStatus: aiStatusSlice.reducer,
    problemCollectionSlice: problemCollectionSlice.reducer,
    ...modals,
};

const rootReducer = combineReducers(reducers);
export const middlewares = [
    apiV2.middleware,
    apiV1.middleware,
    rtkQueryErrorLogger,
    authPersistence,
];

export const store = configureStore({
    reducer: rootReducer,
    preloadedState: {
        ...rootReducer(undefined, {type: '@@INIT'}),
        auth: getStoredAuthState() ?? initialAuthState,
    },
    devTools: import.meta.env.DEV && {
        name: 'GoProblems',
        trace: true,
    },
    // middleware: getDefaultMiddleware =>
    //     getDefaultMiddleware().concat(middlewares),
    // TODO: We store currentNode and root in the board reducer, which is not serializable
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: false,
            thunk: {
                extraArgument: {
                    apiV1BaseQuery: fetch(API_BASE_CONFIG),
                    apiV2BaseQuery: fetch(API_V2_BASE_CONFIG),
                },
            },
        }).concat(middlewares),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

// Set up store getter for transform functions
import {setStoreGetter} from '@components/transforms/transformRelativeGoBoardThumbnails';
setStoreGetter(() => store as any);
